import { CircleInfoIcon } from "@xola/icons";
import { flash, FormGroup, Label, Modal, Spinner, SubmitButton, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Virtuoso } from "react-virtuoso";
import {
    Coupon,
    CouponCode,
    CouponCodeDuplicateOption,
    CouponCodeStatus,
    CouponCodeStatusCopy,
    CreateCouponCode,
    Permission,
} from "types";
import { api } from "../../api/api";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useInfiniteCollection } from "../../hooks/useInfiniteCollection";
import { useInvalidate } from "../../hooks/useInvalidation";
import { ConfirmLeaveModal } from "../Common/ConfirmLeaveModal";
import { NoTranslate } from "../Common/NoTranslate";
import { SearchInput } from "../Common/SearchInput";
import { SecondaryButton } from "../Common/SecondaryButton";
import { ContentLayout } from "../Layout/ContentLayout";
import { Loader } from "../Loader";
import { CouponCodeInput } from "./CouponCodeInput";

interface EditCouponCodesFormProps {
    coupon: Coupon;
    onCancel: () => void;
}

export const EditCouponCodesForm = ({ coupon, onCancel }: EditCouponCodesFormProps) => {
    const [codes, setCodes] = useState<CouponCode[]>([]);
    const [search, setSearch] = useState<string>("");
    const [codeBlankError, setBlankError] = useState(false);
    const [duplicateOption, setDuplicateOption] = useState<CouponCodeDuplicateOption | null>(null);
    const [selectedCouponCode, setSelectedCouponCode] = useState<CouponCode | null>(null);
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();

    const {
        data: couponCodes,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
    } = useInfiniteCollection<CouponCode>(
        ["couponCodes"],
        { "code[contains]": search || null, coupon: coupon.id },
        { keepPreviousData: true },
    );

    const {
        mutateAsync: saveCodes,
        isLoading: isSaving,
        error: saveError,
    } = useMutation(async (payload: CreateCouponCode[]) => {
        return api.post(`/coupons/${coupon.id}/codes`, payload, {
            params: { duplicate: duplicateOption, batch: true },
        });
    });

    const { mutateAsync: voidCode, isLoading: isVoiding } = useMutation(async () => {
        return api.put(`/coupons/${coupon.id}/codes/${selectedCouponCode!.id}`, {
            ...selectedCouponCode,
            status: CouponCodeStatus.VOID,
        });
    });

    const handleVoidClick = async (couponCode: CouponCode) => {
        setSelectedCouponCode(couponCode);
    };

    const handleConfirmVoidClick = async () => {
        await voidCode();
        invalidate("couponCodes", selectedCouponCode?.id);
        setSelectedCouponCode(null);
    };

    const handleCodeInputFocus = () => {
        setBlankError(false);
    };

    const handleSubmit = async () => {
        if (codes.length === 0) {
            setBlankError(true);
            return;
        }

        await saveCodes(codes).then(() => {
            flash.success("Codes successfully added");
            invalidate("codes");
            invalidate("couponCodes");
            setCodes([]);
        });

        setDuplicateOption(null);
        setBlankError(false);
    };

    return (
        <ContentLayout isWide className="space-y-4" isDisabled={!isGranted(Permission.MODIFY_COUPONS)}>
            <ContentLayout.Section
                title="Codes"
                description="Code that must be entered while making a booking to provide the discount you have created."
            >
                <div className="space-y-4">
                    <div className="flex items-center justify-between rounded py-1">
                        <SearchInput className="w-full" placeholder="Quick Search" onChange={setSearch} />
                        {isFetching && <Spinner size="tiny" />}
                    </div>

                    <div className="overflow-hidden rounded border border-gray-light">
                        <div className="flex divide-x divide-gray-light border-b border-gray-light bg-white">
                            <div className="w-72 py-2 px-4 text-md font-bold">Code</div>
                            <div className="flex-1 py-2 px-4 text-md font-bold">Status</div>
                            <div className="flex-1 py-2 px-4 text-md font-bold">Usage</div>
                        </div>
                        {couponCodes.length > 0 ? (
                            <Virtuoso
                                style={{ height: 300 }}
                                data={couponCodes}
                                endReached={async () => {
                                    if (hasNextPage) {
                                        fetchNextPage();
                                    }
                                }}
                                itemContent={(index, couponCode: CouponCode) => {
                                    const isLast = couponCodes.length === index + 1;

                                    return (
                                        <div
                                            className={clsx(
                                                "group flex divide-x divide-gray-light text-gray-darker hover:bg-gray-lighter",
                                                isLast && "border-b border-gray-light",
                                            )}
                                            data-testid="edit-coupon-codes-form-item"
                                        >
                                            <NoTranslate
                                                as="div"
                                                title={couponCode.code.toUpperCase()}
                                                className="w-72 truncate py-2 px-4 font-mono"
                                            >
                                                {couponCode.code.toUpperCase()}
                                            </NoTranslate>

                                            <div
                                                className="flex flex-1 justify-between truncate py-2 px-4"
                                                data-testid="edit-coupon-codes-form-status"
                                            >
                                                {CouponCodeStatusCopy[couponCode.status!]}

                                                {couponCode.status === CouponCodeStatus.ACTIVE ? (
                                                    <SecondaryButton
                                                        className="invisible -my-1 group-hover:visible"
                                                        size="small"
                                                        onClick={async () => handleVoidClick(couponCode)}
                                                    >
                                                        Void
                                                    </SecondaryButton>
                                                ) : null}
                                            </div>

                                            <NoTranslate
                                                as="div"
                                                className="flex-1 truncate py-2 px-4"
                                                data-testid="edit-coupon-codes-form-usage"
                                            >
                                                {couponCode.uses}
                                                {Number(coupon?.limit) > 0 ? `/${coupon.limit}` : null}
                                            </NoTranslate>
                                        </div>
                                    );
                                }}
                                components={{ Footer: () => (isFetchingNextPage ? <Loader /> : null) }}
                            />
                        ) : (
                            !isFetching && (
                                <div className="flex justify-center">
                                    <span className="pb-4 pt-4 text-black">No codes found</span>
                                </div>
                            )
                        )}
                    </div>

                    <FormGroup>
                        <ConfirmLeaveModal isFormTouched={codes.length > 0} />

                        <Label>
                            <span className={clsx(codeBlankError && "text-danger")}>
                                New Codes {codeBlankError && <span>cannot be blank</span>}
                            </span>
                            <Tooltip content="Input multiple codes separated by a comma or a new line.">
                                <CircleInfoIcon />
                            </Tooltip>
                        </Label>

                        <CouponCodeInput
                            isError={!!saveError || codeBlankError}
                            value={codes}
                            error={saveError}
                            duplicateOption={duplicateOption}
                            onFocus={handleCodeInputFocus}
                            onChange={(codes) => setCodes(codes)}
                            onDuplicateOptionChange={(duplicateOption) => setDuplicateOption(duplicateOption)}
                        />
                    </FormGroup>
                </div>
                <ContentLayout.Footer>
                    <SubmitButton type="button" isLoading={isSaving} onClick={handleSubmit}>
                        Save
                    </SubmitButton>

                    <SecondaryButton type="reset" onClick={onCancel}>
                        Cancel
                    </SecondaryButton>
                </ContentLayout.Footer>
            </ContentLayout.Section>

            <Modal isOpen={selectedCouponCode !== null} onClose={() => setSelectedCouponCode(null)}>
                <Modal.Header
                    description={
                        <>
                            Are you sure you want to void code{" "}
                            <var data-var="code">{selectedCouponCode?.code.toUpperCase() ?? ""}</var> This cannot be
                            undone
                        </>
                    }
                >
                    Void Coupon Code
                </Modal.Header>

                <Modal.Footer className="space-x-4">
                    <SecondaryButton disabled={isVoiding} onClick={() => setSelectedCouponCode(null)}>
                        Cancel
                    </SecondaryButton>

                    <SubmitButton isLoading={isVoiding} color="danger" onClick={handleConfirmVoidClick}>
                        Void
                    </SubmitButton>
                </Modal.Footer>
            </Modal>
        </ContentLayout>
    );
};
